import { Collection, Database, Q } from '@nozbe/watermelondb';
import { copyClassInstance } from '../../utils/classes';

import HorseContact from './HorseContact';
import Organisation from './Organisation';
import Location from './Location';
import {
    applyContactsFiltersQueries,
    getUnsafeContactsSearchQueries,
} from '../utils';
import {
    ContactModel,
    ContactPayload,
    ContactPostEditPayload,
} from '../../types/Contacts';
import {
    ContactsFilterOptions,
    ContactsFiltersObject,
} from '../../types/contactsFilters';
import {
    ContactDBServiceOptions,
    DBServiceOptionsWithImagesAndValidation,
} from '../../types/dbService';
import { EntryModel } from '../../types/Entries';
import { HorseModel } from '../../types/Horses';
import { HorseContactModel } from '../../types/HorseContact';
import { LocationModel } from '../../types/Location';
import { SortObject, UnsafeQuery } from '../../types/sort';
import { FILTER_TYPE } from '../../types/filter';
import { WhereDescription } from '@nozbe/watermelondb/QueryDescription';
import {
    addPreparedDataBatch,
    mapRecord,
    mapRecords,
    prepareContactLocation,
    PreparedContactData,
} from './helpers/contact/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddContactActions,
    logBatchAddContactActions,
    prepareContactBatchUserUpdate,
    prepareCreateBatchContact,
    prepareCreateContact,
    prepareCreateHorseContacts,
} from './helpers/contact/contactAdd';
import {
    logUpdateContactActions,
    prepareUpdateContact,
    prepareUpdateEventContact,
    prepareUpdateHorseContacts,
} from './helpers/contact/contactUpdate';
import {
    logDeleteContactActions,
    prepareDeleteContact,
    prepareDeleteEventContacts,
    prepareDeleteHorseContacts,
} from './helpers/contact/contactDelete';

import { Identificator } from '../../types/Identificator';
import { UserModel, UserPayload } from '../../types/User';

import { createDefaultWriteStrategy } from '../operations/createDefaultWriteStrategy';

import { OperationContext } from '../../types/operations';
import { ContactWriteStrategy } from '../../types/writeStrategy';

class Contact {
    private database: Database;
    private collection: Collection<ContactModel>;
    private table = 'contacts';
    private options: ContactDBServiceOptions;
    private writeStrategy: ContactWriteStrategy;

    constructor(options: ContactDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
        this.writeStrategy =
            options.writeStrategy ?? createDefaultWriteStrategy();
    }

    getAll(...queries: Q.Clause[]) {
        return this.collection.query(...queries).fetch();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    getByIDs(ids: Array<string>) {
        return this.collection.query([Q.where('id', Q.oneOf(ids))]).fetch();
    }

    observeCount() {
        return this.collection.query().observeCount();
    }

    async getByParam(param: string, value: any) {
        return this.collection.query(Q.where(param, value)).fetch();
    }

    async getByEntries(entryIds: string[]) {
        return this.collection
            .query(
                Q.experimentalNestedJoin('horse_contacts', 'horses'),
                Q.experimentalNestedJoin('horses', 'entries'),
                Q.on(
                    'horse_contacts',
                    Q.on('horses', Q.on('entries', 'id', Q.oneOf(entryIds))),
                ),
            )
            .fetch();
    }

    async getContactEntries(id: string) {
        const entries: EntryModel[] = [];
        const contact = await this.collection.find(id);
        const contactHorses = await contact.horses.fetch();
        await Promise.all(
            contactHorses.map(async (horse) => {
                const horseEntries = await horse.entries.fetch();
                entries.push(...horseEntries);
            }),
        );
        return entries;
    }

    async getContactsUniqueHorses(
        contacts: ContactModel[],
        queries: Q.Clause[],
    ): Promise<HorseModel[]> {
        const horses: HorseModel[] = [];

        await Promise.all(
            contacts.map(async (contact) => {
                const contactHorses = await contact.horses
                    .extend(...queries)
                    .fetch();

                horses.push(...contactHorses);
            }),
        );

        return [...new Set(horses)];
    }

    public getFilterContactsQueries(
        sort?: SortObject | null,
        filters?: ContactsFiltersObject,
    ): [Q.Clause[], Q.Clause[]] {
        const queries: Q.Clause[] = [];

        if (filters) {
            queries.push(
                Q.experimentalJoinTables(['horse_contacts', 'invoices']),
                Q.experimentalNestedJoin('horse_contacts', 'horses'),
                Q.experimentalNestedJoin('horses', 'entries'),
                ...applyContactsFiltersQueries(filters),
            );
        }

        const sortQueries: Q.Clause[] = [];

        if (sort && sort.query && sort.value) {
            const sortQuery = sort.query(sort.value);

            sortQueries.push(sortQuery);
        }

        return [queries, sortQueries];
    }

    public getFilterContactsSearchLokiQuery({
        searchText,
        sort,
        includeBusinessName,
    }: Pick<
        ContactsFilterOptions,
        'searchText' | 'includeBusinessName' | 'sort'
    >): Q.Clause {
        const parsedSearchText = searchText.toLowerCase().trim();

        const unsafeSearchQueries = getUnsafeContactsSearchQueries(
            parsedSearchText,
            includeBusinessName,
        );

        //@ts-ignore
        return Q.unsafeLokiTransform((raws, loki) => {
            let result = raws;

            unsafeSearchQueries.forEach(
                (query: UnsafeQuery) => (result = query(result, loki)),
            );

            if (sort && sort.unsafeQuery)
                result = sort.unsafeQuery(result, sort.value, loki);

            return result;
        });
    }

    public getFilterContactsSearchSQLQuery({
        searchText,
        includeBusinessName,
    }: Pick<ContactsFilterOptions, 'searchText' | 'includeBusinessName'>) {
        const parsedSearchText = searchText.toLowerCase().trim();

        const sanitizedSearch = Q.sanitizeLikeString(parsedSearchText);

        if (!sanitizedSearch.length) return;

        const queries: Array<Q.SqlExpr | WhereDescription> = [
            Q.unsafeSqlExpr(
                `first_name || ' ' || ifnull(last_name, '') LIKE '${sanitizedSearch}%'`,
            ),
            Q.unsafeSqlExpr(
                `first_name || ' ' || ifnull(last_name, '') LIKE '% ${sanitizedSearch}%'`,
            ),
        ];

        if (includeBusinessName) {
            queries.push(
                Q.where('business_name', Q.like(`${sanitizedSearch}%`)),
                Q.where('business_name', Q.like(`% ${sanitizedSearch}%`)),
            );
        }

        return Q.or(
            // @ts-ignore
            ...queries,
        );
    }

    private filterContacts({
        searchText,
        filterType,
        sort,
        filters,
        includeBusinessName,
    }: ContactsFilterOptions) {
        const [filterQueries, sortQueries] = this.getFilterContactsQueries(
            sort,
            filters,
        );

        const queries = [...filterQueries];

        const searchQuery =
            filterType === FILTER_TYPE.LOKI
                ? this.getFilterContactsSearchLokiQuery({
                      searchText,
                      sort,
                      includeBusinessName,
                  })
                : this.getFilterContactsSearchSQLQuery({
                      searchText,
                      includeBusinessName,
                  });

        if (searchQuery) queries.push(searchQuery);

        return this.collection.query(...queries).extend(...sortQueries);
    }

    getFilteredContactsCount({
        searchText,
        filterType,
        filters,
        includeBusinessName = false,
    }: ContactsFilterOptions) {
        const query = this.filterContacts({
            searchText,
            filterType,
            filters,
            includeBusinessName,
        });

        return query.fetchCount();
    }

    getFilteredContacts({
        searchText,
        filterType,
        filters,
        sort,
        includeBusinessName = false,
    }: ContactsFilterOptions) {
        const query = this.filterContacts({
            searchText,
            filterType,
            sort,
            filters,
            includeBusinessName,
        });

        return query.fetch();
    }

    async add(payload: ContactPayload, userId: string) {
        const { horses, image, location } = payload;
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        };
        const organisationService = new Organisation(serviceOptions);
        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        const newContact = await this.database.write(async () => {
            let preparedData: PreparedContactData[] = [];
            const { preparedLocation, existingLocation } =
                await prepareContactLocation({
                    location,
                    userId,
                    organisationID,
                    database: this.database,
                });

            if (preparedLocation) {
                preparedData.push(
                    mapRecord(preparedLocation, 'locations', 'add'),
                );
            }

            const createdContact = prepareCreateContact({
                payload,
                userId,
                organisationID,
                locationElement: preparedLocation || existingLocation,
                collection: this.collection,
            });
            preparedData.push(mapRecord(createdContact, 'contacts', 'add'));

            const horseContactsToAdd = prepareCreateHorseContacts({
                horses,
                contact: createdContact,
                database: this.database,
            });
            preparedData.push(
                ...mapRecords(horseContactsToAdd, 'horse_contacts', 'add'),
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logAddContactActions({
                contact: createdContact,
                horseContactsToAdd,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return createdContact;
        });

        if (image && image.uri) {
            await this.options.imageService.uploadImage({
                image,
                entityID: newContact.id,
                entityType: 'Contact',
                annotationImage: '',
                ownerID: userId,
                userIDs: [userId],
                organisationID,
            });
        }

        return newContact;
    }

    async batchAdd(
        contacts: ContactPayload[],
        userId: string,
        postPayload?: { userPayload?: UserPayload },
    ) {
        let contactsToAdd: ContactModel[] = [];
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        };
        const organisationService = new Organisation(serviceOptions);
        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];
        const imagesToUpload: Array<{
            payload: ContactPayload;
            contact: ContactModel;
        }> = [];

        await this.database.write(async () => {
            let preparedData: PreparedContactData[] = [];
            const locationsToAdd: LocationModel[] = [];
            const horseContactsToAdd: HorseContactModel[] = [];
            const preparedLocationsByPlaceId = new Map<string, LocationModel>();

            for (const payload of contacts) {
                const { preparedLocation, existingLocation } =
                    await prepareContactLocation({
                        location: payload.location,
                        userId,
                        organisationID,
                        database: this.database,
                        preparedLocationsByPlaceId,
                    });

                if (
                    preparedLocation &&
                    !locationsToAdd.includes(preparedLocation)
                ) {
                    locationsToAdd.push(preparedLocation);
                    preparedData.push(
                        mapRecord(preparedLocation, 'locations', 'add'),
                    );
                }

                const contactToAdd = prepareCreateBatchContact({
                    payload,
                    userId,
                    organisationID,
                    locationElement: preparedLocation || existingLocation,
                    collection: this.collection,
                });
                contactsToAdd = [...contactsToAdd, contactToAdd];
                preparedData.push(mapRecord(contactToAdd, 'contacts', 'add'));

                const createdHorseContacts = prepareCreateHorseContacts({
                    horses: payload.horses,
                    contact: contactToAdd,
                    database: this.database,
                });
                horseContactsToAdd.push(...createdHorseContacts);
                preparedData.push(
                    ...mapRecords(
                        createdHorseContacts,
                        'horse_contacts',
                        'add',
                    ),
                );

                if (payload.image?.uri) {
                    imagesToUpload.push({ payload, contact: contactToAdd });
                }
            }

            const userToUpdate = postPayload?.userPayload
                ? prepareContactBatchUserUpdate({
                      user: await this.database.collections
                          .get<UserModel>('users')
                          .find(userId),
                      userPayload: postPayload.userPayload,
                  })
                : undefined;
            if (userToUpdate) {
                preparedData.push(mapRecord(userToUpdate, 'users', 'update'));
            }

            validateEntityRecords(preparedData, this.options.validation);

            await addPreparedDataBatch(preparedData, this.database);

            logBatchAddContactActions({
                locationsToAdd,
                horseContactsToAdd,
                contactsToAdd,
                userToUpdate,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });
        });

        await Promise.all(
            imagesToUpload.map(({ payload, contact }) =>
                this.options.imageService.uploadImage({
                    image: payload.image,
                    entityID: contact.id,
                    entityType: 'Contact',
                    annotationImage: '',
                    ownerID: userId,
                    userIDs: [userId],
                    organisationID,
                }),
            ),
        );

        return contactsToAdd;
    }

    async update({
        id,
        payload,
        userId,
        getPostPayload,
    }: {
        id: string;
        payload: Partial<ContactPayload>;
        userId: string;
        getPostPayload?: (
            updatedContact: ContactModel,
        ) => ContactPostEditPayload | undefined;
    }): Promise<ContactModel> {
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        };
        const organisationService = new Organisation(serviceOptions);
        const organisation = await organisationService.get();
        const locationService = new Location(serviceOptions);
        const { id: organisationID } = organisation[0];
        const contactElement = await this.getByID(id);
        const { horses, image, removeAvatar } = payload;
        const initialHorses = await contactElement.horses.fetch();
        let horseContacts: HorseContactModel[] = [];

        if (horses) {
            const horseContactService = new HorseContact(serviceOptions);
            horseContacts = await horseContactService.getByParam(
                'contact_id',
                contactElement.id,
            );
        }

        const updateContactResult = await this.database.write(async () => {
            let preparedData: PreparedContactData[] = [];
            const { preparedLocation, existingLocation } =
                await prepareContactLocation({
                    location: payload.location,
                    userId,
                    organisationID,
                    database: this.database,
                });

            if (preparedLocation) {
                preparedData.push(
                    mapRecord(preparedLocation, 'locations', 'add'),
                );
            }
            let fallbackLocationExists = false;

            if (contactElement.locationId) {
                const contactLocation = await locationService.getByParam(
                    'id',
                    contactElement.locationId,
                );
                fallbackLocationExists = contactLocation.length > 0;
            }
            const updatedContact = prepareUpdateContact({
                contactElement,
                payload,
                locationElement: preparedLocation || existingLocation,
                fallbackLocationExists,
                logError: this.options.logger.error,
                entityName: this.table,
            });
            preparedData.push(mapRecord(updatedContact, 'contacts', 'update'));

            const horseContactsCollection =
                this.database.collections.get<HorseContactModel>(
                    'horse_contacts',
                );
            const { horseContactsToAdd, horseContactsToDelete } =
                prepareUpdateHorseContacts({
                    horses,
                    initialHorses,
                    horseContacts,
                    contactElement,
                    horseContactsCollection,
                });

            preparedData.push(
                ...mapRecords(horseContactsToAdd, 'horse_contacts', 'add'),
                ...mapRecords(
                    horseContactsToDelete,
                    'horse_contacts',
                    'delete',
                ),
            );

            const updatedEventContact = await prepareUpdateEventContact({
                database: this.database,
                contact: updatedContact,
                payload: getPostPayload?.(updatedContact),
            });
            if (updatedEventContact) {
                preparedData.push(
                    mapRecord(updatedEventContact, 'event_contacts', 'update'),
                );
            }

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logUpdateContactActions({
                locationElement: preparedLocation,
                contactElement,
                horseContactsToAdd,
                horseContactsToDelete,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            if (updatedEventContact) {
                this.options.logger.logDBAction({
                    message: 'Update contact - update event contact',
                    modelName: this.table,
                    payload: updatedEventContact,
                });
            }
            return updatedContact;
        });

        if (removeAvatar) {
            await this.options.imageService.remove(id);
        }

        if (image && image.uri) {
            await this.options.imageService.uploadImage({
                image,
                entityID: id,
                entityType: 'Contact',
                annotationImage: '',
                userIDs: [userId],
                documentID: image.documentID,
                organisationID,
            });
        }

        return copyClassInstance(updateContactResult);
    }

    async prepareHorseContacts({
        contactElement,
        horses,
    }: {
        contactElement: ContactModel;
        horses: Identificator[];
    }) {
        const initialHorses = await contactElement.horses.fetch();
        const horseContactsCollection =
            this.database.collections.get<HorseContactModel>('horse_contacts');

        const horseContactService = new HorseContact({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const horseContacts = await horseContactService.getByParam(
            'contact_id',
            contactElement.id,
        );

        let horseContactsToAdd: HorseContactModel[] = [];
        let horseContactsToDelete: HorseContactModel[] = [];

        if (!!horses) {
            await Promise.all(
                horses.map(async (horse) => {
                    if (
                        !initialHorses.find(
                            (initialHorse) => initialHorse.id === horse.id,
                        )
                    ) {
                        const horseContactToAdd =
                            await horseContactsCollection.prepareCreate(
                                (horseContact) => {
                                    horseContact.contactId = contactElement.id;
                                    horseContact.userId = contactElement.userId;
                                    horseContact.organisationId =
                                        contactElement.organisationId;
                                    horseContact.horseId = horse.id;
                                },
                            );

                        horseContactsToAdd = [
                            ...horseContactsToAdd,
                            horseContactToAdd,
                        ];
                    }
                }),
            );
        }

        if (!!horses) {
            await Promise.all(
                horseContacts.map((horseContact) => {
                    if (!horses.find(({ id }) => id === horseContact.horseId)) {
                        horseContactsToDelete = [
                            ...horseContactsToDelete,
                            horseContact.prepareMarkAsDeleted(),
                        ];
                    }
                }),
            );
        }

        return { horseContactsToAdd, horseContactsToDelete };
    }

    async prepareBumpUpdate(id: string) {
        const contactElement = await this.getByID(id);
        return contactElement.prepareUpdate((contact: ContactModel) => {
            contact.firstName = contactElement.firstName;
        });
    }

    async updateField(
        id: string,
        fieldName: string,
        fieldValue: string | number | boolean,
    ) {
        const contactElement = await this.getByID(id);

        await this.database.write(async () => {
            const preparedData: PreparedContactData[] = [];
            const contactToUpdate = contactElement.prepareUpdate(
                (contact) => (contact[fieldName] = fieldValue),
            );

            preparedData.push(mapRecord(contactToUpdate, 'contacts', 'update'));

            validateEntityRecords(preparedData, this.options.validation);

            await addPreparedDataBatch(preparedData, this.database);

            this.options.logger.logDBAction({
                message: `Update contact field ${fieldName}`,
                modelName: this.table,
                payload: contactElement,
            });
        });
    }

    async deleteByID(
        id: string,
        options?: { operationContext?: OperationContext },
    ) {
        const contactElement = await this.getByID(id);
        const contactHorses = await contactElement.horses.fetch();
        const contactEvents = await contactElement.events.fetch();

        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        };

        await this.database.write(async () => {
            let preparedData: PreparedContactData[] = [];
            const contactToDelete = prepareDeleteContact({ contactElement });

            preparedData.push(mapRecord(contactToDelete, 'contacts', 'delete'));

            const horseContactsToDelete = await prepareDeleteHorseContacts({
                contactId: id,
                shouldDelete: contactHorses.length > 0,
                serviceOptions,
            });
            preparedData.push(
                ...mapRecords(
                    horseContactsToDelete,
                    'horse_contacts',
                    'delete',
                ),
            );

            const { eventContactsToDelete, eventContactRemindersToDelete } =
                await prepareDeleteEventContacts({
                    contactId: id,
                    shouldDelete: contactEvents.length > 0,
                    serviceOptions,
                });
            preparedData.push(
                ...mapRecords(
                    eventContactsToDelete,
                    'event_contacts',
                    'delete',
                ),
                ...mapRecords(
                    eventContactRemindersToDelete,
                    'event_contact_reminders',
                    'delete',
                ),
            );

            validateEntityRecords(preparedData, this.options.validation);
            await this.writeStrategy.commitPreparedData({
                preparedData,
                database: this.database,
                transactionAction: 'delete',
                operationContext: options?.operationContext,
            });

            logDeleteContactActions({
                contactElement,
                horseContactsToDelete,
                eventContactsToDelete,
                eventContactRemindersToDelete,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });
        });

        try {
            await this.options.imageService.remove(id);
        } catch (error) {
            this.options.logger.error(error);
        }
    }
}

export default Contact;
